import {
  type ComponentType,
  type LazyExoticComponent,
  lazy,
  type ReactNode,
} from 'react';
import {
  Book,
  Bot,
  Home,
  Languages,
  Lightbulb,
  Pencil,
  Rss,
  Wallet,
} from '../../shared/icons';

export type AppKey =
  | 'home'
  | 'notebook'
  | 'ledger'
  | 'bot'
  | 'rss'
  | 'korean'
  | 'english'
  | 'homeassistant'
  | 'ocrtest';

export interface AppDef {
  key: AppKey;
  /** 挂载路径前缀（也是 dock 点击后导航的地址） */
  path: string;
  title: string;
  description: string;
  icon: (props: { className?: string }) => ReactNode;
  /** 子应用根组件；home 自身是启动器，没有组件 */
  Component?: LazyExoticComponent<ComponentType>;
  /** 隐藏应用：可直达但不进 dock（测试页等临时入口） */
  hidden?: boolean;
}

/** 启动器（home 视图） */
export const HOME: AppDef = {
  key: 'home',
  path: '/',
  title: 'home',
  description: '应用启动器',
  icon: Home,
};

// 各子应用注册表：新增应用时在这里登记即可，dock 与启动器会自动出现
export const apps: AppDef[] = [
  {
    key: 'notebook',
    path: '/notebook/',
    title: 'notebook',
    description: 'Markdown 笔记本，以文件树组织你的想法',
    icon: Book,
    Component: lazy(() => import('../notebook/App')),
  },
  {
    key: 'ledger',
    path: '/ledger/',
    title: 'ledger',
    description: '记账本，SQLite 持久化，月历视图浏览每日收支',
    icon: Wallet,
    Component: lazy(() => import('../ledger/App')),
  },
  {
    key: 'bot',
    path: '/bot/',
    title: 'bot',
    description: 'QQ 机器人配置：凭据与技能管理',
    icon: Bot,
    Component: lazy(() => import('../bot/App')),
  },
  {
    key: 'rss',
    path: '/rss/',
    title: 'rss',
    description: '订阅管理，md 单文件存频道，勾选即已看',
    icon: Rss,
    Component: lazy(() => import('../rss/App')),
  },
  {
    key: 'korean',
    path: '/korean/',
    title: 'korean',
    description: '韩语词汇本，自录词条，间隔重复背词',
    icon: Languages,
    Component: lazy(() => import('../korean/App')),
  },
  {
    key: 'english',
    path: '/english/',
    title: 'english',
    description: '英语词汇本，自录词条，间隔重复背词',
    icon: Languages,
    Component: lazy(() => import('../english/App')),
  },
  {
    key: 'homeassistant',
    path: '/homeassistant/',
    title: 'homeassistant',
    description: '智能家居控制，接入 Home Assistant 开关灯光',
    icon: Lightbulb,
    Component: lazy(() => import('../homeassistant/App')),
  },
  {
    // spike：手写识别测试页（白板默写可行性评估），验证后可能删除
    key: 'ocrtest',
    path: '/ocr-test/',
    title: 'ocr-test',
    description: '手写识别测试页（隐藏）',
    icon: Pencil,
    Component: lazy(() => import('../ocr-test/App')),
    hidden: true,
  },
];

/** 按浏览器路径推断当前激活的应用，未知路径回退到 home */
export function selectKey(pathname: string): AppKey {
  for (const app of apps) {
    if (pathname.startsWith(app.path.replace(/\/$/, ''))) return app.key;
  }
  return HOME.key;
}
